import { useEffect, useState } from 'react';
import { createApiRef, useApi } from '@backstage/frontend-plugin-api';
import type {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/**
 * Reads the Claude plugin marketplace through the backend `marketplace` plugin.
 *
 * The browser cannot reach the repository directly (CORS, and the token must
 * not ship in the bundle). It also no longer assembles anything: it used to
 * fetch the raw manifest AND the whole recursive repo tree on every page load,
 * then — on a plugin page — wait for those before fanning out to plugin.json,
 * the README, hooks.json and one file per skill/agent/command. The backend now
 * answers each page with the shape that page renders, from a cache (see
 * packages/backend/src/modules/marketplace).
 *
 * Request count per page, cold:
 *
 *   /marketplace                  1   (was 2, each a GitHub round trip)
 *   /marketplace/<plugin>         1   (was 2, then 3 + N after them)
 *   /marketplace/<plugin>/<item>  2   (index, then the file; was 2 → 3 + N → 1)
 */

/**
 * The response shapes. Restated from the backend's `assemble.ts`, which the app
 * package cannot import — change both together.
 */

/** The shape Claude Code itself defines in `.claude-plugin/marketplace.json`. */
export interface MarketplaceManifest {
  name: string;
  owner?: { name?: string; email?: string };
  metadata?: { description?: string; version?: string };
  plugins: ManifestPlugin[];
}

export interface ManifestPlugin {
  name: string;
  source: string;
  description?: string;
  version?: string;
  author?: { name?: string; email?: string };
  keywords?: string[];
  category?: string;
}

export type ItemKind = 'skill' | 'agent' | 'command';

/** A skill, agent or command — one markdown file, discovered from the tree. */
export interface PluginItem {
  kind: ItemKind;
  /** Directory name for skills, file basename for agents and commands. */
  name: string;
  /** Repo-relative path to the markdown file. */
  path: string;
}

export interface PluginContents {
  skills: PluginItem[];
  agents: PluginItem[];
  commands: PluginItem[];
  /** Present when the plugin ships hooks/hooks.json. */
  hooksPath?: string;
  hasReadme: boolean;
}

export interface MarketplacePlugin extends ManifestPlugin {
  /** `source` is declared as './plugins/<dir>'; this is just <dir>. */
  dir: string;
  contents: PluginContents;
}

export interface Marketplace {
  manifest: MarketplaceManifest;
  plugins: MarketplacePlugin[];
}

/** hooks.json, as Claude Code reads it. */
export interface HooksFile {
  description?: string;
  hooks: Record<string, HookMatcher[]>;
}

export interface HookMatcher {
  matcher?: string;
  hooks: { type: string; command: string; timeout?: number }[];
}

/** plugin.json — the per-plugin manifest, richer than the marketplace entry. */
export interface PluginManifest {
  name: string;
  description?: string;
  version?: string;
  author?: { name?: string; email?: string };
  homepage?: string;
  repository?: string;
  license?: string;
  keywords?: string[];
}

/** A plugin's own manifest, readme, hooks and the frontmatter of each item. */
export interface PluginDetail {
  plugin: MarketplacePlugin;
  manifest?: PluginManifest;
  /** Frontmatter already stripped, server-side. */
  readme?: string;
  hooks?: HooksFile;
  /** Frontmatter descriptions, keyed by repo path. */
  descriptions: Record<string, string>;
}

/** One markdown file, split server-side into frontmatter and body. */
export interface MarkdownFile {
  path: string;
  frontmatter: Record<string, string>;
  body: string;
}

/** Thrown when the manifest lists no plugin by that name. */
export class PluginNotFoundError extends Error {
  constructor(pluginName: string) {
    super(`The marketplace has no plugin named "${pluginName}".`);
    this.name = 'PluginNotFoundError';
  }
}

/**
 * What the pages talk to. Registered as an API rather than constructed inside
 * hooks, which is what every other data source in this app does — and what makes
 * the caching below possible, since one instance is shared by every page.
 */
export interface MarketplaceApi {
  /** The manifest plus the contents of every plugin. */
  getMarketplace(): Promise<Marketplace>;
  /** One plugin's manifest, readme, hooks and per-item frontmatter. */
  getPluginDetail(pluginName: string): Promise<PluginDetail>;
  /** One markdown file, by repo-relative path. */
  getFile(path: string): Promise<MarkdownFile>;
  /**
   * Values already in hand, synchronously — what a page renders on its FIRST
   * frame. Without this every mount started empty and painted a loading state
   * for at least one render, even when the data had been loaded a click ago.
   */
  peekMarketplace(): Marketplace | undefined;
  peekPluginDetail(pluginName: string): PluginDetail | undefined;
  peekFile(path: string): MarkdownFile | undefined;
  /** Human-facing link into the repository, for "View source". */
  sourceUrl(path?: string): string;
}

export const marketplaceApiRef = createApiRef<MarketplaceApi>({
  id: 'plugin.marketplace.service',
});

/**
 * sessionStorage, for an instant first paint after a reload: the last answer is
 * shown at once and replaced when the fresh one arrives. Per tab, gone when the
 * tab closes. Every access is wrapped — storage can be absent, full, or throw
 * outright (private windows, blocked site data) — and the page must work the
 * same without it, only slower.
 */
const STORAGE_PREFIX = 'marketplace:v1:';

const readStored = <T>(key: string): T | undefined => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

const writeStored = (key: string, value: unknown) => {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota or disabled storage: the in-memory copy still serves this tab.
  }
};

export class MarketplaceClient implements MarketplaceApi {
  #base?: Promise<string>;
  // Every read is memoised on the instance. Without it, opening one skill
  // refetched the manifest, the whole repo tree, the plugin manifest, its readme
  // and the frontmatter of every sibling item — because each hook built its own
  // client and called the one above it. The cost is that a plugin published
  // mid-session appears on the next full page load rather than the next
  // navigation; the backend's cache bounds how stale that load can be.
  readonly #pending = new Map<string, Promise<unknown>>();
  readonly #resolved = new Map<string, unknown>();
  readonly #hydrated = new Map<string, unknown>();

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly configApi: ConfigApi,
  ) {}

  sourceUrl(path?: string): string {
    const browseUrl = this.configApi.getOptionalString('marketplace.browseUrl');
    if (!browseUrl) {
      return '';
    }
    if (!path) {
      return browseUrl;
    }
    const template =
      this.configApi.getOptionalString('marketplace.browsePathTemplate') ??
      '/blob/main/{path}';
    return `${browseUrl}${template.replace('{path}', path)}`;
  }

  // The backend `marketplace` plugin, not the proxy. A proxy could not carry
  // this credential: its Authorization header is fixed when config loads, and
  // the deployment authenticates to GitHub as an App whose installation token
  // expires hourly. The proxy therefore carried a personal access token, that
  // token's account was not an org member, and every read of the private
  // marketplace repo answered 404 with this page rendering empty.
  async #baseUrl(): Promise<string> {
    this.#base ??= this.discoveryApi.getBaseUrl('marketplace');
    return this.#base;
  }

  async #get<T>(path: string): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.#baseUrl()}${path}`);
    if (!res.ok) {
      throw Object.assign(
        new Error(
          `Marketplace request failed: ${res.status} ${res.statusText} (${path})`,
        ),
        { status: res.status, response: res },
      );
    }
    return (await res.json()) as T;
  }

  /**
   * One shared promise per key. A failure is not kept: a backend that was
   * briefly down would otherwise keep the page broken for the whole session.
   */
  #load<T>(key: string, fetcher: () => Promise<T>, persist: boolean) {
    let pending = this.#pending.get(key) as Promise<T> | undefined;
    if (!pending) {
      pending = fetcher().then(
        value => {
          this.#resolved.set(key, value);
          if (persist) writeStored(key, value);
          return value;
        },
        error => {
          this.#pending.delete(key);
          throw error;
        },
      );
      this.#pending.set(key, pending);
    }
    return pending;
  }

  /**
   * `valid` guards what sessionStorage hands back: a deploy that changes a
   * response's shape without bumping STORAGE_PREFIX would otherwise feed the
   * old JSON to the first render and crash it before the fetch replaces it.
   */
  #peek<T>(
    key: string,
    persisted: boolean,
    valid: (value: unknown) => boolean = () => true,
  ): T | undefined {
    if (this.#resolved.has(key)) {
      return this.#resolved.get(key) as T;
    }
    if (!persisted) return undefined;
    // Parsed once per key: pages peek on every render, and the stored index is
    // not worth re-parsing each time. A later fetch supersedes it above.
    if (!this.#hydrated.has(key)) {
      const stored = readStored<T>(key);
      this.#hydrated.set(key, valid(stored) ? stored : undefined);
    }
    return this.#hydrated.get(key) as T | undefined;
  }

  getMarketplace(): Promise<Marketplace> {
    return this.#load('index', () => this.#get<Marketplace>('/index'), true);
  }

  peekMarketplace(): Marketplace | undefined {
    return this.#peek('index', true, v =>
      Array.isArray((v as Marketplace | undefined)?.plugins),
    );
  }

  getPluginDetail(pluginName: string): Promise<PluginDetail> {
    return this.#load(
      `plugin:${pluginName}`,
      async () => {
        try {
          return await this.#get<PluginDetail>(
            `/plugins/${encodeURIComponent(pluginName)}`,
          );
        } catch (e) {
          // Only the backend's NAMED 404 means "no such plugin". In local
          // development the plugin mounts no routes, so every request 404s —
          // and that must show the error state, not a confident "not found".
          const { status, response } = e as {
            status?: number;
            response?: Response;
          };
          if (status === 404 && response) {
            const body = (await response.json().catch(() => ({}))) as {
              error?: { name?: string };
            };
            if (body.error?.name === 'PluginNotFoundError') {
              throw new PluginNotFoundError(pluginName);
            }
          }
          throw e;
        }
      },
      true,
    );
  }

  peekPluginDetail(pluginName: string): PluginDetail | undefined {
    return this.#peek(`plugin:${pluginName}`, true, v =>
      Boolean((v as PluginDetail | undefined)?.plugin?.contents),
    );
  }

  getFile(path: string): Promise<MarkdownFile> {
    // Not persisted: an item body can be long, and the in-memory copy covers
    // the back-and-forth that matters.
    return this.#load(
      `file:${path}`,
      () =>
        this.#get<MarkdownFile>(
          `/files/${path.split('/').map(encodeURIComponent).join('/')}`,
        ),
      false,
    );
  }

  peekFile(path: string): MarkdownFile | undefined {
    return this.#peek(`file:${path}`, false);
  }
}

/**
 * One cached read, keyed. Renders whatever is already in hand on the first
 * frame, then whatever the request returns.
 *
 * Changing `key` swaps to THAT key's cached value (or nothing) rather than
 * blanking first — the old hook reset to undefined on every navigation, so even
 * a value loaded a click ago flashed a progress bar. If a background reload
 * fails while a value is showing, the value stays: an error panel over data
 * that is a few minutes old helps nobody.
 */
const useCached = <T>(
  key: string | undefined,
  peek: () => T | undefined,
  load: () => Promise<T>,
) => {
  const [state, setState] = useState<{
    key?: string;
    value?: T;
    error?: Error;
  }>(() => ({ key, value: key ? peek() : undefined }));

  useEffect(() => {
    if (!key) {
      setState({ key });
      return undefined;
    }
    let active = true;
    setState(s => (s.key === key ? s : { key, value: peek() }));
    load()
      .then(value => active && setState({ key, value }))
      .catch(
        e =>
          active &&
          setState(s =>
            // Keep what is on screen through a transient failure — but not
            // through a definite "this plugin no longer exists", or a removed
            // plugin renders from sessionStorage instead of its 404.
            s.key === key &&
            s.value !== undefined &&
            !(e instanceof PluginNotFoundError)
              ? s
              : { key, error: e },
          ),
      );
    return () => {
      active = false;
    };
    // `peek` and `load` close over `key` and the api; `key` is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Guard against the one render between a key change and the effect above.
  const current =
    state.key === key ? state : { value: undefined, error: undefined };
  return {
    value: current.value,
    error: current.error,
    loading: Boolean(key) && !current.value && !current.error,
  };
};

/** Manifest plus the contents of every plugin, in one request. */
export const useMarketplace = () => {
  const api = useApi(marketplaceApiRef);
  const { value, error, loading } = useCached(
    'index',
    () => api.peekMarketplace(),
    () => api.getMarketplace(),
  );
  return { data: value, error, loading };
};

/**
 * The marketplace's entry for one plugin, if the index is already in hand. No
 * request — this is what the plugin page shows (name, description, item names)
 * while its own detail is on the way, when you arrived from the browse page.
 */
export const useCachedPluginSummary = (
  pluginName: string,
): MarketplacePlugin | undefined =>
  useApi(marketplaceApiRef)
    .peekMarketplace()
    ?.plugins.find(p => p.name === pluginName);

export const usePluginDetail = (pluginName: string) => {
  const api = useApi(marketplaceApiRef);
  const { value, error, loading } = useCached(
    pluginName,
    () => api.peekPluginDetail(pluginName),
    () => api.getPluginDetail(pluginName),
  );

  // A name that is not in the manifest is a 404 for the page, not an error
  // panel — the distinction the page renders on.
  const notFound = error instanceof PluginNotFoundError;

  return {
    detail: value,
    error: notFound ? undefined : error,
    notFound,
    loading,
  };
};

/** One markdown file — a skill, agent or command. */
export const useMarkdownFile = (path?: string) => {
  const api = useApi(marketplaceApiRef);
  const { value, error, loading } = useCached(
    path,
    () => (path ? api.peekFile(path) : undefined),
    () => api.getFile(path!),
  );
  return { file: value, error, loading };
};

/** The repository link for a path, for components that only need the URL. */
export const useSourceUrl = (path?: string) =>
  useApi(marketplaceApiRef).sourceUrl(path);
